import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { DeniedState } from './DeniedState';
import { PageHeader } from './PageHeader';
import { Skeleton } from './Skeleton';

// Closed-beta gate for the admin.developerz.ai views. A `developerz-ai` org
// staff member (flagged `is_platform_admin` on `/v1/me`) sees the platform
// radar; everyone else — signed out, or an outsider who logged in via SSO —
// hits a "not yet, closed beta" wall. While `useMe` resolves we hold a neutral
// checking state so a staff user never flashes the wall before their content.
// `token` is injectable for tests; the router mounts routes with no props.
//
// The checking state is `<QueryBoundary/>`'s loading shape — a skeleton under a
// `role="status"` carrying sr-only text — so it reads as every other load and
// `e2e/story-gate.ts` waits it out the same way. The wall is `<DeniedState/>`:
// being outside the beta is an answer about who may see this, not a fault.

export function AdminGate(props: { token?: string | null; children: JSX.Element }): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const me = useMe(token);
  return (
    <Show
      when={!me.isLoading}
      fallback={
        <div role="status">
          <span class="sr-only">{t('admin.gate_checking')}</span>
          <Skeleton height="8rem" />
        </div>
      }
    >
      <Show
        when={me.data?.is_platform_admin}
        fallback={
          <>
            <PageHeader title={t('admin.gate_title')} />
            <DeniedState message={t('admin.gate_wall')} />
          </>
        }
      >
        {props.children}
      </Show>
    </Show>
  );
}
